(function () {
  class VideoSlider extends HTMLElement {
    static observedAttributes = ["data-index"];

    constructor() {
      super();

      this.sliderButtons = this.querySelectorAll(".video-slider__button");
      this.thumbnailVideos = this.querySelectorAll(
        ".video-slider__button video",
      );
      this.slideTemplates = this.querySelectorAll("template");
      this.modal = this.querySelector(".video-slide__modal");
      this.modalPlaceholder = document.createComment("video-slide__modal");
      this.modalSlider = null;

      this.handleButtonEvent = this.handleButtonEvent.bind(this);
      this.handleModalClick = this.handleModalClick.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleModalVideoEvent = this.handleModalVideoEvent.bind(this);
    }

    get currentIndex() {
      return Number(this.dataset.index || 0);
    }

    set currentIndex(value) {
      this.dataset.index = Number(value) || 0;
    }

    connectedCallback() {
      this.mountModalPortal();
      this.toggleEventListeners(true);
    }

    disconnectedCallback() {
      this.closeModal();
      this.toggleEventListeners(false);
      this.unmountModalPortal();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === null || oldValue === newValue) {
        return;
      }
      if (name === "data-index") {
        const index = Number(newValue);
        this.sliderButtons.forEach((button) =>
          button.classList.remove("video-slider__button--active"),
        );
        this.sliderButtons[index]?.classList.add("video-slider__button--active");
      }
    }

    handleButtonEvent(event) {      
      const { currentTarget, type } = event;
      const buttonElement = currentTarget.closest("button");
      if (!buttonElement) return;
      const videoElement = buttonElement.querySelector("video");
      const index = Number(buttonElement.dataset.slideIndex || 0);

      // if (!videoElement) return;

      switch (type) {
        case "pointerenter": {
          this.currentIndex = index;
          this.playThumbnailVideo(videoElement);
          break;
        }
        case "pointerleave": {
          this.pauseThumbnailVideos();
          break;
        }
        case "click": {
          this.currentIndex = index;
          if (this.modalSlider === null) {
            this.insertModalContent();
            this.initializeSlider()
              .then((response) => {
                this.modalSlider = response;
                this.openModal();
                this.attachEventToSlider(this.modalSlider.element.swiper);
              })
              .catch((error) => console.error(error));
          } else {
            this.openModal();
          }
          break;
        }
        default:
          break;
      }
    }

    playThumbnailVideo(video) {
      this.pauseThumbnailVideos();
      this.safePlay(video);
    }

    pauseThumbnailVideos() {
      this.thumbnailVideos.forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });
    }

    playModalVideo(video) {
      this.pauseModalVideos();
      this.safePlay(video);
      video.removeAttribute("paused");
    }

    pauseModalVideos() {
      if (!this.modal) return;
      const buttonPlayGroups = this.modal.querySelectorAll(".video-card__control-play");
      const buttonSoundGroups = this.modal.querySelectorAll(".video-card__control-sound");
      this.modal.querySelectorAll("video").forEach((video, index) => {
        video.pause();
        video.currentTime = 0;
        video.setAttribute("paused", "");
        if (buttonPlayGroups[index]) buttonPlayGroups[index].setAttribute("data-active-type", "pause");
        if (buttonSoundGroups[index]) buttonSoundGroups[index].setAttribute("data-active-type", "muted");
      });
    }

    safePlay(video) {
      if (!video) return;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    }

    insertModalContent() {
      if (!this.modal) return;
      const inner = this.modal.querySelector(".video-slide__modal-wrapper");
      const clonedContents = [...this.slideTemplates].map((template) =>
        template.content.cloneNode(true),
      );
      if (inner) {
        clonedContents.forEach((clonedContent) => {
          const clonedVideo = clonedContent.querySelector("video");
          const clonedVideoCard = clonedContent.querySelector(".video-card");
          if (clonedVideo) clonedVideo.blur();
          if (clonedVideoCard) clonedVideoCard.focus({ preventScroll: true });
          inner.appendChild(clonedContent);
        });
        this.initializeDurationLines(inner);
        this.attachEventsToControl(inner);
        this.attachEventsToProductCard(inner);
      }
    }

    initializeDurationLines(container) {
      const slides = container.querySelectorAll(".video-slide__modal-slide");
      slides.forEach((slide) => {
        const video = slide.querySelector(".video-card__media video");
        const durationLine = slide.querySelector(".video-card__duration");
        if (!video || !durationLine) return;

        this.updateDurationLine(video, durationLine);

        ["play", "pause", "timeupdate", "ended", "loadedmetadata"].forEach(
          (eventName) =>
            video.addEventListener(eventName, this.handleModalVideoEvent),
        );
      });
    }

    handleModalVideoEvent(event) {
      const video = event.currentTarget;
      const slide = video.closest(".video-slide__modal-slide");
      const durationLine = slide?.querySelector(".video-card__duration");
      if (!durationLine) return;

      this.updateDurationLine(video, durationLine);
    }

    updateDurationLine(video, durationLine) {
      const fallbackDuration = this.getDurationFromDataset(durationLine);
      const sourceDuration =
        Number.isFinite(video.duration) && video.duration > 0
          ? video.duration
          : fallbackDuration;

      if (!sourceDuration) {
        durationLine.style.setProperty("--video-progress", "0%");
        return;
      }

      const progress = Math.min(
        Math.max(video.currentTime / sourceDuration, 0),
        1,
      );
      durationLine.style.setProperty("--video-progress", `${+progress.toFixed(2) * 100}%`);
    }

    getDurationFromDataset(durationLine) {
      const rawDuration = Number(durationLine.dataset.duration);
      if (!Number.isFinite(rawDuration) || rawDuration <= 0) {
        return 0;
      }

      return rawDuration > 1000 ? rawDuration / 1000 : rawDuration;
    }

    initializeSlider() {
      if (!this.modal) {
        return Promise.reject(new Error("Modal element is missing"));
      }
      const swiperElement = this.modal.querySelector(".swiper");
      if (!swiperElement) {
        return Promise.reject(new Error("Swiper container is missing"));
      }

      if (window.swiperFile && window.swiperFile.is_loaded === true) {
        return Promise.resolve(new SwiperConfigurator(swiperElement));
      }

      return SwiperConfigurator.appendSwiperFiles().then(
        () => new SwiperConfigurator(swiperElement),
      );
    }

    openModal() {
      if (!this.modal) return;
      this.toggleAttributeForModal(true);
      if (
        this.modalSlider &&
        this.modalSlider.element &&
        this.modalSlider.element.swiper
      ) {
        const { element: { swiper: slider } } = this.modalSlider;
        const slides = slider.wrapperEl.querySelectorAll(".swiper-slide");
        if (slides[this.currentIndex]) {
          slider.slideTo(this.currentIndex, 0, false);
          const activeSlide = slides[this.currentIndex];
          const activeSlideVideo = activeSlide?.querySelector("video");
          if (activeSlideVideo) {
            this.playModalVideo(activeSlideVideo);
          }
        }
      }
    }

    closeModal() {
      if (!this.modal) return;
      this.toggleAttributeForModal(false);
      this.pauseModalVideos();
    }

    toggleAttributeForModal(isOpen) {
      this.modal.classList.toggle("video-slide__modal--open", isOpen);
      this.modal.toggleAttribute("hidden", !isOpen);
      document.body.classList.toggle("overflow-hidden", isOpen);
    }

    mountModalPortal() {
      if (!this.modal || this.modal.parentNode === document.body) return;

      this.modal.replaceWith(this.modalPlaceholder);
      document.body.appendChild(this.modal);
    }

    unmountModalPortal() {
      if (!this.modal || this.modal.parentNode !== document.body) return;
      this.modalPlaceholder.replaceWith(this.modal);
    }

    handleModalClick(event) {
      if (!this.modal?.classList.contains("video-slide__modal--open")) return;

      if (
        event.target === this.modal ||
        event.target.closest(".video-card__content-close")
      ) {
        this.closeModal();
      }
    }

    handleKeyDown(event) {
      if (event.key === "Escape") {
        this.closeModal();
      }
    }

    toggleEventListeners(shouldAttach = true) {
      const method = shouldAttach ? "addEventListener" : "removeEventListener";
      const buttonEventTypes = ["click"];

      if (window.matchMedia("(pointer: fine)").matches) {
        buttonEventTypes.push("pointerenter", "pointerleave");
      }

      this.sliderButtons.forEach((button) => {
        buttonEventTypes.forEach((eventName) =>
          button[method](eventName, this.handleButtonEvent),
        );
      });

      if (this.modal) {
        this.modal[method]("click", this.handleModalClick);
        document[method]("keydown", this.handleKeyDown);
      }
    }

    attachEventToSlider(slider) {
      slider.on("slideChange", (event) => {
        const { activeIndex, slides } = event;
        const activeSlide = slides[activeIndex];
        this.currentIndex = activeIndex;
        const activeSlideVideo = activeSlide?.querySelector("video");
        if (activeSlideVideo) {
          this.playModalVideo(activeSlideVideo);
        }
      });
    }

    attachEventsToControl(container) {
      const controlMethods = (video) => ({
        play: () => {
          this.safePlay(video);
          video.removeAttribute("paused");
        },
        pause: () => {
          video.pause();
          video.setAttribute("paused", "");
        },
        muted: () => { video.volume = 0; },
        unmuted: () => { video.volume = 0.5; },
      });

      const controlAttributes = {
        play: "pause",
        pause: "play",
        muted: "unmuted",
        unmuted: "muted",
      };

      const cardPlayButtons = container.querySelectorAll(".video-card__play");
      const controlLines = container.querySelectorAll(".video-card__control");
      controlLines.forEach((controlLine) =>
        controlLine.addEventListener("click", (event) => {
          const buttonGroup = event.target.closest("[data-active-type]");
          const button = event.target.closest("button");
          if (button && buttonGroup) {
            const card = button.closest(".video-card");
            const video = card?.querySelector("video");
            const { name } = button;
            if (video && ["play", "pause", "muted", "unmuted"].includes(name)) {
              buttonGroup.setAttribute("data-active-type", controlAttributes[name]);
              controlMethods(video)[name]();
            }
          }
        }),
      );

      cardPlayButtons.forEach((cardPlayButton, index) => {
        cardPlayButton.addEventListener("click", () => {
          const playButton = controlLines[index]?.querySelector("button[name='play']");
          playButton?.click();
        });
      });
    }

    attachEventsToProductCard(container) {
      const toggleDialog = (dialog, isOpen) => {
        dialog?.classList.toggle("video-dialog--active", isOpen);
      };

      container.addEventListener("click", (event) => {
        const productCard = event.target.closest(".video-card__product");
        const overlay = event.target.closest(".video-dialog__overlay");
        const videoCard = event.target.closest(".video-card");

        if (!videoCard) return;

        const video = videoCard.querySelector("video");
        const dialog = videoCard.querySelector(".video-dialog");

        if (productCard) {
          video?.pause();
          toggleDialog(dialog, true);
        }

        if (overlay) {
          this.safePlay(video);
          toggleDialog(dialog, false);
        }
      });
    }
  }

  if (!window.customElements.get("video-slider")) {
    window.customElements.define("video-slider", VideoSlider);
  }
})();
